/**
 * IMPORTS
 */

import { useMemo } from "react";

import { DynamicTable } from "@components/table-custom/table";

import { BarraFiltrosCobrancaCredito } from "@pages/admin/editar-dados-cliente/cobranca-creditos-cliente/components/barra-filtros-cobranca-credito";
import { ResumoConsumoCredito } from "@pages/admin/editar-dados-cliente/cobranca-creditos-cliente/components/resumo-consumo-credito";

import { useMinhaCobrancaCreditos } from "@/hooks/cobranca-credito-cliente/buscar-minha-cobranca";

import type { ILinhaMinhaCobrancaCredito } from "@/interface/extrato-credito-cliente";
import { getStatusLabel } from "@/utils/get-status-label";

function formatarDataHoraCobranca(isoData: string): string {
  try {
    return new Date(isoData).toLocaleString("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return isoData;
  }
}

function formatarQuantidadeCredito(quantidade: number): string {
  return Number.isInteger(quantidade) ? String(quantidade) : quantidade.toFixed(2);
}

function formatarPeriodoResumo(dataInicio?: string, dataFim?: string): string {
  if (!dataInicio && !dataFim) {
    return "Todos os períodos";
  }
  try {
    const fmt = (iso: string) =>
      new Date(`${iso}T12:00:00`).toLocaleDateString("pt-BR", { dateStyle: "short" });
    if (dataInicio && dataFim) {
      return `${fmt(dataInicio)} — ${fmt(dataFim)}`;
    }
    if (dataInicio) {
      return `A partir de ${fmt(dataInicio)}`;
    }
    return `Até ${fmt(dataFim!)}`;
  } catch {
    return "Período selecionado";
  }
}

function gerarPaginasNavegacao(currentPage: number, lastPage: number): Array<number | string> {
  if (lastPage <= 1) {
    return [1];
  }

  const paginas: Array<number | string> = [1];
  const inicioJanela = Math.max(2, currentPage - 1);
  const fimJanela = Math.min(lastPage - 1, currentPage + 1);

  if (inicioJanela > 2) {
    paginas.push("...");
  }
  for (let pagina = inicioJanela; pagina <= fimJanela; pagina += 1) {
    paginas.push(pagina);
  }
  if (fimJanela < lastPage - 1) {
    paginas.push("...");
  }
  paginas.push(lastPage);

  return paginas;
}

function mapearLinhaParaTabela(linha: ILinhaMinhaCobrancaCredito) {
  const situacao = String(linha.remessa?.situacao ?? linha.remessa?.status ?? "-");
  const rotuloSituacao = getStatusLabel(situacao);

  return {
    id: linha.id,
    dataHoraMovimentacao: formatarDataHoraCobranca(linha.created_at),
    nomeProduto: linha.produto?.nome ?? "-",
    numeroRemessa: linha.remessa?.numero_remessa ? `#${linha.remessa.numero_remessa}` : "-",
    quantidadeCreditosFormatada: formatarQuantidadeCredito(linha.quantidade_creditos),
    situacaoRemessa:
      rotuloSituacao !== "Status desconhecido" ? rotuloSituacao : situacao.replace(/_/g, " "),
  };
}

const colunasMinhaCobranca = [
  { label: "Data", accessor: "dataHoraMovimentacao" },
  { label: "Produto", accessor: "nomeProduto", limit: 40 },
  { label: "Remessa", accessor: "numeroRemessa" },
  { label: "Qtd. créditos", accessor: "quantidadeCreditosFormatada" },
  { label: "Situação", accessor: "situacaoRemessa", isBadge: true },
];

/**
 * Cobrança de créditos do cliente logado: mesma visão da cobrança em
 * editar dados do cliente (backoffice), sem exibir valores em R$.
 */
function MinhaCobrancaCreditos() {
  const {
    linhas,
    paginacao,
    totais,
    totaisPorProduto,
    carregando,
    buscando,
    erro,
    paginaAtual,
    itensPorPagina,
    definirPaginaAtual,
    filtros,
    definirFiltros,
    limparFiltros,
    aplicarFiltroMesAtual,
    opcoesProduto,
  } = useMinhaCobrancaCreditos({ habilitado: true });

  const resumoPeriodo = useMemo(
    () => formatarPeriodoResumo(filtros.dataInicio, filtros.dataFim),
    [filtros.dataInicio, filtros.dataFim]
  );

  const linhasTabela = useMemo(() => linhas.map(mapearLinhaParaTabela), [linhas]);

  const ultimaPagina = paginacao?.last_page ?? 1;
  const paginasNavegacao = useMemo(
    () => gerarPaginasNavegacao(paginaAtual, ultimaPagina),
    [paginaAtual, ultimaPagina]
  );

  const onGoToPage = (page: number | "prev" | "next") => {
    if (page === "prev") {
      definirPaginaAtual(Math.max(1, paginaAtual - 1));
      return;
    }
    if (page === "next") {
      definirPaginaAtual(Math.min(ultimaPagina, paginaAtual + 1));
      return;
    }
    definirPaginaAtual(page);
  };

  return (
    <div className="space-y-4">
      {erro && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Não foi possível carregar a cobrança. Tente novamente em instantes.
        </div>
      )}

      <ResumoConsumoCredito
        totais={totais}
        totaisPorProduto={totaisPorProduto}
        carregando={carregando || buscando}
        resumoPeriodo={resumoPeriodo}
        exibirValorTotal={false}
      />

      <DynamicTable
        title="Minha cobrança"
        description="Consumo de créditos das suas remessas no período selecionado."
        isLoadingPage={carregando}
        data={linhasTabela}
        totalItems={paginacao?.total ?? 0}
        itemsPerPage={itensPorPagina}
        columns={colunasMinhaCobranca}
        paginations={paginasNavegacao}
        onGoToPage={onGoToPage}
        currentPage={paginaAtual}
        lastPage={ultimaPagina}
        childerSearch={
          <BarraFiltrosCobrancaCredito
            filtros={filtros}
            onAlterarFiltros={definirFiltros}
            onLimparFiltros={limparFiltros}
            onAplicarMesAtual={aplicarFiltroMesAtual}
            opcoesProduto={opcoesProduto}
            carregandoProdutos={carregando}
          />
        }
      />
    </div>
  );
}

/**
 * EXPORTS
 */
export { MinhaCobrancaCreditos };
